// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Permanent Progress Manager
 * 
 * Tracks mastery levels that persist across game resets.
 * Mastery = highest tier upgrade ever purchased per category.
 * 
 * Stored under a key that is preserved during localStorage.clear() reset.
 */

const PROGRESS_KEY = 'starship_permanent_progress';

const MASTERY_BONUSES = {
  shields:  { label: 'Shield Mastery',  perLevel: [0, 0.05, 0.10, 0.15], unit: 'shield capacity' },
  hull:     { label: 'Hull Mastery',    perLevel: [0, 5, 10, 15],        unit: 'hull points' },
  engines:  { label: 'Engine Mastery',  perLevel: [0, 0.03, 0.06, 0.10], unit: 'fuel efficiency' },
  sensors:  { label: 'Sensor Mastery',  perLevel: [0, 0.05, 0.10, 0.15], unit: 'scan bonus' },
  special:  { label: 'Special Mastery', perLevel: [0, 0.05, 0.10, 0.15], unit: 'starting credits' },
};

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(PROGRESS_KEY) || '{}');
  } catch { return {}; }
};

const save = (data) => {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(data));
};

/**
 * Record an upgrade purchase. Updates mastery if this is a new highest tier.
 * @param {string} category - shields, hull, engines, sensors, special
 * @param {number} tier - 1, 2, or 3
 */
export const recordUpgrade = (category, tier) => {
  if (!MASTERY_BONUSES[category] || tier < 1 || tier > 3) return;
  const data = load();
  if (!data.mastery) data.mastery = {};
  const current = data.mastery[category] || 0;
  if (tier > current) {
    data.mastery[category] = tier;
    save(data);
  }
};

/**
 * Get mastery level for a category.
 * @param {string} category
 * @returns {number} 0-3
 */
export const getMasteryLevel = (category) => {
  const data = load();
  return data.mastery?.[category] || 0;
};

/**
 * Get the bonus value for a category at its current mastery level.
 * @param {string} category
 * @returns {number} Bonus value (percentage or flat depending on category)
 */
export const getMasteryBonus = (category) => {
  const level = getMasteryLevel(category);
  const config = MASTERY_BONUSES[category];
  return config ? config.perLevel[level] : 0;
};

/**
 * Get all mastery data for display.
 * @returns {Array} Array of { category, label, level, bonus, unit }
 */
export const getAllMastery = () => {
  return Object.entries(MASTERY_BONUSES).map(([category, config]) => {
    const level = getMasteryLevel(category);
    return {
      category,
      label: config.label,
      level,
      maxLevel: 3,
      bonus: config.perLevel[level],
      nextBonus: level < 3 ? config.perLevel[level + 1] : null,
      unit: config.unit,
    };
  });
};

/** Storage key — exported so reset logic can preserve it */
export const PROGRESS_STORAGE_KEY = PROGRESS_KEY;
